import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../models/Logging';
import {
	invalidateEmailExecBriefs,
	isActiveBriefRun,
	isTerminalBriefRunStatus,
	useEmailExecBriefsQuery,
} from '../queries/EmailExecAssistant/useEmailExecBriefsQuery';
import { invalidateLatestEmailExecDigest } from '../queries/EmailExecAssistant/useLatestEmailExecDigestQuery';
import { useRunEmailExecTemplateMutation } from '../queries/EmailExecAssistant/useRunEmailExecTemplateMutation';

const RUN_NOW_FALLBACK_ERROR = "We couldn't finish your brief. Please try again.";

export const inFlightRunStatusText = (run: Api.IEmailExecBriefSystemJob): string => {
	if (run.runTrigger === Api.AgentRunTrigger.Scheduled) {
		return 'Your scheduled brief is running now.';
	}
	return run.status === 'InProgress' ? 'Generating your brief…' : 'Queued…';
};

/**
 * The Run Now lifecycle shared by the settings panel, the history page, and the dashboard card.
 * In-flight truth comes from the briefs list query (which also covers scheduled runs and survives
 * reloads), not from a per-job poll; completion toasts fire only for the run started through this
 * hook instance.
 */
export function useRunEmailExecBrief({
	enabled,
	source,
}: {
	enabled?: boolean;
	source: 'settings' | 'historyPage' | 'dashboardCard';
}) {
	const { logEvent } = useEventLogging('EmailExecRunBrief');
	const briefsQuery = useEmailExecBriefsQuery({ enabled });

	const runMutation = useRunEmailExecTemplateMutation({
		onSuccess: job => {
			// Over the daily cap the endpoint returns 200 with an already-Failed job carrying the reason.
			if (job.status === 'Failed') {
				logEvent('EmailExecRunNow-Error', { rejected: true, hasServerMessage: !!job.errorMessage, source });
				toast.error(job.errorMessage || RUN_NOW_FALLBACK_ERROR);
				return;
			}
		},
		onError: error => {
			logEvent('EmailExecRunNow-Error', { source });
			// A 400's systemMessage is user-facing copy (e.g. "A brief is already running…"); anything else is internal.
			const friendlyMessage = error.systemCode === 400 ? error.systemMessage : null;
			toast.error(friendlyMessage || "We couldn't run your brief. Please try again.");
		},
		onSettled: () => {
			invalidateEmailExecBriefs();
		},
	});

	const startedJob = runMutation.data;
	// A cap-rejected start arrives already Failed — never treat it as the active run.
	const startedRunId = startedJob && startedJob.status !== 'Failed' ? startedJob.id : undefined;

	const runs = briefsQuery.data;
	const inFlightRun = runs?.find(isActiveBriefRun);

	const startedRunInList = startedRunId ? runs?.find(run => run.id === startedRunId) : undefined;
	const resetRunMutation = runMutation.reset;
	React.useEffect(() => {
		if (!startedRunInList || !isTerminalBriefRunStatus(startedRunInList.status)) {
			return;
		}
		resetRunMutation();
		if (startedRunInList.status === 'Success') {
			invalidateLatestEmailExecDigest();
			toast.success('Your brief is on its way to your inbox.');
		} else {
			logEvent('EmailExecRunNow-Error', { hasServerMessage: !!startedRunInList.errorMessage, source });
			toast.error(startedRunInList.errorMessage || RUN_NOW_FALLBACK_ERROR);
		}
	}, [startedRunInList, resetRunMutation, logEvent, source]);

	const runBrief = (templateId: string) => {
		logEvent('EmailExecRunNowClicked', { source });
		runMutation.mutate(templateId);
	};

	const isRunning = runMutation.isPending || !!startedRunId || !!inFlightRun;

	return { runBrief, isRunning, inFlightRun, isStartPending: runMutation.isPending };
}
